import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface RfqMergedItem {
  description: string; unit: string | null; totalQty: number; estimatedRate: number;
  requisitions: string[]; lastVendor: string | null; lastPrice: number | null; lastPurchaseDate: string | null;
}
export interface RfqMerged { items: RfqMergedItem[]; requisitionCount: number }

export interface RfqListItem {
  id: number; rfqCode: string; title: string | null; status: 'open' | 'closed'; notes: string | null;
  createdAt: string | null; itemCount: number; quoteCount: number;
}

export interface RfqQuoteRate { quoteId: number; rate: number | null }
export interface RfqItem {
  id: number; description: string; unit: string | null; quantity: number;
  rates: RfqQuoteRate[]; quotedCount: number; compared: boolean;
  lowestRate: number | null; lowestQuoteId: number | null;
}
export interface RfqQuote {
  id: number; vendorId: number | null; vendorName: string | null; quotationUrl: string | null;
  quotationNumber: string | null; quotationDate: string | null; receivedAt: string | null; remarks: string | null;
  total: number; quotedLines: number; wins: number;
}
export interface RfqDetail {
  id: number; rfqCode: string; title: string | null; status: 'open' | 'closed'; notes: string | null;
  createdAt: string | null; rfqDate: string | null;
  items: RfqItem[]; quotes: RfqQuote[]; winnerQuoteId: number | null;
}

export interface AddQuoteInput {
  vendorId?: number | null; vendorName?: string | null;
  vendorGstin?: string | null; vendorEmail?: string | null; vendorMobile?: string | null;
  quotationNumber?: string | null; quotationDate?: string | null;
  quotationUrl?: string | null; remarks?: string | null;
  lines: Array<{ rfqItemId: number; rate: number }>;
}

export interface QuoteExtractResult {
  configured: boolean;
  vendorName: string | null;
  vendorGstin: string | null;
  vendorEmail: string | null;
  vendorMobile: string | null;
  quotationNumber: string | null;
  quotationDate: string | null;
  message: string | null;
  lines: Array<{ rfqItemId: number; rate: number }>;
  matchedCount: number;
  itemCount: number;
}

export interface RfqPurchaseOrder {
  id: number; poCode: string; rfqCode: string; status: string;
  vendorName: string | null; totalAmount: number; quotationUrl: string | null; itemCount: number; createdAt: string | null;
}
export interface RfqPoItem { id: number; description: string; unit: string | null; quantity: number; rate: number; amount: number }
export interface RfqPurchaseOrderDetail {
  id: number; poCode: string; rfqCode: string; status: string;
  approvalStatus: 'pending' | 'approved' | 'rejected'; approvalRemarks: string | null;
  poDate: string | null; quotationNumber: string | null; quotationDate: string | null;
  vendorId: number | null; vendorName: string | null; vendorGst: string | null; vendorPan: string | null;
  vendorAddress: string | null; vendorContact: string | null; vendorMobile: string | null; vendorEmail: string | null;
  deliveryAddress: string | null; deliveryTerms: string | null; paymentTerms: string | null;
  totalAmount: number; quotationUrl: string | null; remarks: string | null; createdAt: string | null;
  items: RfqPoItem[];
}
export interface DeliveryAddress { id: number; label: string | null; address: string }

export const epcRfqApi = {
  listPurchaseOrders: async (params: Record<string, unknown> = {}): Promise<{ data: RfqPurchaseOrder[]; meta: PaginationMeta }> => {
    const { data } = await apiClient.get<ApiSuccess<RfqPurchaseOrder[]>>('/epc-rfq/purchase-orders', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  getPurchaseOrder: async (poId: number): Promise<RfqPurchaseOrderDetail> =>
    (await apiClient.get<ApiSuccess<RfqPurchaseOrderDetail>>(`/epc-rfq/purchase-orders/${poId}`)).data.data,
  listDeliveryAddresses: async (): Promise<DeliveryAddress[]> =>
    (await apiClient.get<ApiSuccess<DeliveryAddress[]>>('/epc-rfq/delivery-addresses')).data.data,
  approvePurchaseOrder: async (poId: number, input: { deliveryAddress: string; deliveryLabel?: string | null; deliveryTerms?: string | null; poDate?: string | null }): Promise<RfqPurchaseOrderDetail> =>
    (await apiClient.post<ApiSuccess<RfqPurchaseOrderDetail>>(`/epc-rfq/purchase-orders/${poId}/approve`, input)).data.data,
  rejectPurchaseOrder: async (poId: number, remarks?: string | null): Promise<RfqPurchaseOrderDetail> =>
    (await apiClient.post<ApiSuccess<RfqPurchaseOrderDetail>>(`/epc-rfq/purchase-orders/${poId}/reject`, { remarks })).data.data,
  modifyPurchaseOrder: async (poId: number, input: { items: Array<{ id?: number; description: string; unit?: string | null; quantity: number; rate: number }>; deliveryTerms?: string | null }): Promise<RfqPurchaseOrderDetail> =>
    (await apiClient.put<ApiSuccess<RfqPurchaseOrderDetail>>(`/epc-rfq/purchase-orders/${poId}`, input)).data.data,
  downloadPurchaseOrderPdf: async (poId: number, poCode: string): Promise<void> => {
    const res = await apiClient.get(`/epc-rfq/purchase-orders/${poId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = `PO-${poCode}.pdf`; a.click();
    URL.revokeObjectURL(url);
  },
  mergedItems: async (): Promise<RfqMerged> => (await apiClient.get<ApiSuccess<RfqMerged>>('/epc-rfq/merged-items')).data.data,
  list: async (params: Record<string, unknown> = {}): Promise<{ data: RfqListItem[]; meta: PaginationMeta }> => {
    const { data } = await apiClient.get<ApiSuccess<RfqListItem[]>>('/epc-rfq', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  generate: async (input: { title?: string | null; notes?: string | null } = {}): Promise<RfqDetail> =>
    (await apiClient.post<ApiSuccess<RfqDetail>>('/epc-rfq/generate', input)).data.data,
  getById: async (id: number): Promise<RfqDetail> => (await apiClient.get<ApiSuccess<RfqDetail>>(`/epc-rfq/${id}`)).data.data,
  extractQuote: async (id: number, quotationUrl: string): Promise<QuoteExtractResult> =>
    (await apiClient.post<ApiSuccess<QuoteExtractResult>>(`/epc-rfq/${id}/extract-quote`, { quotationUrl })).data.data,
  addQuote: async (id: number, input: AddQuoteInput): Promise<RfqDetail> =>
    (await apiClient.post<ApiSuccess<RfqDetail>>(`/epc-rfq/${id}/quotes`, input)).data.data,
  removeQuote: async (id: number, quoteId: number): Promise<RfqDetail> =>
    (await apiClient.delete<ApiSuccess<RfqDetail>>(`/epc-rfq/${id}/quotes/${quoteId}`)).data.data,
  setStatus: async (id: number, status: 'open' | 'closed'): Promise<RfqDetail> =>
    (await apiClient.post<ApiSuccess<RfqDetail>>(`/epc-rfq/${id}/status`, { status })).data.data,
};
